import { getAvailableDevices, updateDeviceList } from '../../react/features/base/devices/actions';
import {
    JitsiConferenceEvents,
    JitsiMediaDevicesEvents
} from '../../react/features/base/lib-jitsi-meet';
import { getParticipantCount } from '../../react/features/base/participants/functions';
import { getUserSelectedCameraDeviceId } from '../../react/features/base/settings/functions';
import Env from '../modules/Environment';
import WildixEvents from '../service/WildixEvents';
import WildixUIEvents from '../service/WildixUIEvents';

import CameraControll from './CameraControll';
import { WIZYCONF_4K_CAMERA, WIZYCONF_PTZ_CAMERA } from './constants';
import KeyboardControl from './keyboard/keyboardControll';

const MAIN_SCREEN_KEY = '';
const LEFT_SCREEN_KEY = '_1';
const RIGHT_SCREEN_KEY = '_2';


export default class Wizy {
    constructor() {
        this.screenKeys = [ LEFT_SCREEN_KEY, MAIN_SCREEN_KEY ];
        this.flipObject = {
            vertical: 'vertical',
            noFlip: 'no_flip'
        };
        this.availableDevicesArray = [];
        this.videoDevicesArray = [];
        this.keyboardControlInstance = null;
        this.cameraControlInstance = null;
        this.init();

        document.addEventListener(WildixEvents.CONFERENCE_LOADED, () => {
            this.initControls();
        });
        document.addEventListener(WildixEvents.CONFERENCE_JOINED, () => {
            this.#conferenceLoaded();
        });
    }

    #initEvents = () => {
        const conferenceRoom = APP.conference.__getJitsiConference();

        const elRemoteVideos = document.querySelector('.remote-videos');

        conferenceRoom.on(JitsiConferenceEvents.USER_LEFT, (id, user) => {
            if (user.isHidden() || !Env.isWizyconf()) {
                return;
            }

            APP.wildix.eventEmitter.emit(WildixUIEvents.REMOTE_VIDEO_DESTROYED, id);
            this.fixUserAvatarAfterLeave(elRemoteVideos);
        });

        conferenceRoom.addEventListener(JitsiConferenceEvents.USER_JOINED, () => {
            APP.wildix.eventEmitter.emit(WildixUIEvents.REMOTE_VIDEO_CREATED);
        });

        conferenceRoom.addEventListener(JitsiConferenceEvents.TRACK_ADDED, track => {
            if (track && track.isLocal() && track.isVideoTrack()) {
                this._callWizyMethod('toggleFlipVideo', track);
            }
        });

        JitsiMeetJS.mediaDevices.addEventListener(JitsiMediaDevicesEvents.DEVICE_LIST_CHANGED, () => {
            this._callWizyMethod('getDeviceList');
        });
    };

    /**
     *
     * @param name
     * @param data
     * @private
     */
    _callWizyMethod(name, data) {
        if (APP.wildix.wizy) {
            APP.wildix.wizy[name](data);
        }
    }

    #conferenceLoaded = () => {
        this.#initEvents();
        this._callWizyMethod('getDeviceList');
    };

    init = () => {
        this.checkSupportedScreens();
        window.onload = () => {
            switch (this.screenKeys.length) {
            case 2:
                document.body.classList.add('is-double-monitor');
                break;
            case 3:
                document.body.classList.add('is-triple-monitor');
                break;
            }
        };
    };

    initKeyboardControl = () => {
        if (this.keyboardControlInstance) {
            this.keyboardControlInstance.destroy();
        }
        this.keyboardControlInstance = new KeyboardControl();
    }

    initCameraControl() {
        if (this.cameraControlInstance) {
            this.cameraControlInstance.destroy();
        }
        this.cameraControlInstance = new CameraControll();
    }

    initControls() {
        this.initKeyboardControl();
        this.initCameraControl();
    }

    checkSupportedScreens = () => {
        if (Env.isTripleMonitor()) {
            this.screenKeys = [ LEFT_SCREEN_KEY, MAIN_SCREEN_KEY, RIGHT_SCREEN_KEY ];
        } else if (Env.isDualMonitor()) {
            this.screenKeys = [ LEFT_SCREEN_KEY, MAIN_SCREEN_KEY ];
        } else {
            this.screenKeys = [ MAIN_SCREEN_KEY ];
        }
    };


    switchVideoInputDevice = () => {
        const webcams = APP.store.getState()['features/base/devices'].availableDevices.videoInput;
        const selectedCameraDeviceId = getUserSelectedCameraDeviceId(APP.store.getState());
        let webcamPos = webcams.findIndex(d => d.deviceId === selectedCameraDeviceId);

        if (webcams.length > 1) {
            webcamPos++;
            if (webcamPos > webcams.length - 1) {
                webcamPos = 0;
            }
            APP.conference.onVideoDeviceChanged(webcams[webcamPos].deviceId);
        }
    };

    getDeviceList = () => {
        APP.store.dispatch(getAvailableDevices()).then(devices => {
            this.availableDevicesArray = devices;

            this.sortDeviceList(devices);
            this.pushVideoDevices();
        });
    };

    /**
     */
    pushVideoDevices = () => {
        this.videoDevicesArray = [];
        this.availableDevicesArray.forEach(device => {
            if (device.kind === 'videoinput') {
                this.videoDevicesArray.push(device);
            }
        });
    };

    /**
     * Sort list of devices.
     * @param {MediaDeviceInfo[]|object[]} devices
     * @returns {MediaDeviceInfo[]|object[]}
     */
    sortDeviceList = devices => {
        const camsSortPriority = [ WIZYCONF_4K_CAMERA, WIZYCONF_PTZ_CAMERA, 'other' ];

        const cams = [];
        const sortedCams = [];
        const result = [];

        devices.forEach(device => {
            if (device.kind === 'videoinput') {
                cams.push(device);
            } else {
                result.push(device);
            }
        });

        camsSortPriority.forEach(model => {
            if (model !== 'other') {
                const camsByModel = cams.filter(cam => cam.label.toLowerCase().indexOf(model) !== -1);

                camsByModel.length && sortedCams.push(...camsByModel);

                return;
            }

            const otherCams = cams
                .filter(cam => !sortedCams.includes(cam))
                .sort((a, b) => a.label < b.label ? -1 : 1);

            otherCams.length && sortedCams.push(...otherCams);
        });

        sortedCams.length && result.push(...sortedCams);
        APP.store.dispatch(updateDeviceList(result));
    };

    /**
     * @param stream
     */
    toggleFlipVideo = stream => {
        const flipVideoStorage = localStorage.getItem('flipVideo');

        if (flipVideoStorage) {
            const rotateValue = flipVideoStorage === 'true' ? this.flipObject.vertical : this.flipObject.noFlip;

            stream.track.applyConstraints({ advanced: [ { imageFlip: rotateValue } ] });

        }
    };

    /**
     * @param currentElement
     */
    fixUserAvatarAfterLeave = currentElement => {
        if (!currentElement) {
            return false;
        }
        const participantCount = getParticipantCount(APP.store.getState());

        if (participantCount <= 1) {
            currentElement.style.transform = 'translateZ(0)';
        } else if (currentElement.style.transform !== 'none') {
            currentElement.style.transform = 'none';
        }
    }

    /**
     * @param stream
     */
    applyMicGainSetting = stream => {
        const micGainStorage = localStorage.getItem('micGain');

        if (!micGainStorage) {
            return stream;
        }

        const ctx = new AudioContext();
        const convertValue = (Number(micGainStorage) / 33.33).toFixed(2);
        const source = ctx.createMediaStreamSource(stream);
        const dest = ctx.createMediaStreamDestination();
        const gainNode = ctx.createGain();

        source.connect(gainNode);
        gainNode.connect(dest);
        gainNode.gain.value = convertValue; // Any number higher than 0

        return dest.stream;
    }

}
